import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getSilentMode,
  getSilentModeType, 
  setSilentMode, 
  getPaperFormats, 
  saveAllPaperFormats, 
  setPaperFormat, 
  getPaperFormatForArea,
  getLocalPrinter,
  setLocalPrinter,
  clearLocalPrinter,
  getCachedBluetoothDevice,
  setCachedBluetoothDevice,
  restoreBluetoothDevice,
  getSavedBluetoothDeviceName,
  clearSavedBluetoothDeviceName,
  imprimirViaBluetooth,
  imprimirComandaCliente 
} from '../services/clientPrinterService';

// ─── Componentes Auxiliares de UI ──────────────────────────────────────
const MsgError = ({ msg }) => msg ? (
  <div style={{
    background: '#fff1f2',
    borderLeft: '4px solid #f43f5e',
    color: '#9f1239',
    padding: '0.85rem 1.1rem',
    borderRadius: 10,
    marginBottom: '1.25rem',
    fontSize: '0.92rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    boxShadow: '0 2px 8px rgba(244,63,94,0.08)'
  }}>
    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
    <span>{msg}</span>
  </div>
) : null;

const MsgExito = ({ msg }) => msg ? (
  <div style={{
    background: '#f0fdf4',
    borderLeft: '4px solid #10b981',
    color: '#065f46',
    padding: '0.85rem 1.1rem',
    borderRadius: 10,
    marginBottom: '1.25rem',
    fontSize: '0.92rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    boxShadow: '0 2px 8px rgba(16,185,129,0.08)'
  }}>
    <span style={{ fontSize: '1.2rem' }}>✅</span>
    <span>{msg}</span>
  </div>
) : null;

const ConfiguracionComponent = () => {
  const { auth } = useAuth();
  const userId = auth?.user?.id;
  const userName = auth?.user?.nombre || auth?.user?.name || auth?.user?.username || auth?.user?.email || 'Usuario';

  const [modoEmision, setModoEmisionState] = useState('silencioso'); // 'silencioso' | 'confirmacion'
  const [localPrinter, setLocalPrinterState] = useState(null);
  const [formatosPapel, setFormatosPapelState] = useState(80);
  const [pisoSeleccionado, setPisoSeleccionado] = useState(1);
  const [exito, setExito] = useState('');
  const [error, setError] = useState('');
  const [probandoFormato, setProbandoFormato] = useState(null);
  const [btDeviceCache, setBtDeviceCache] = useState(getCachedBluetoothDevice());
  const [vinculandoBt, setVinculandoBt] = useState(false);
  const [probandoConexion, setProbandoConexion] = useState(false);
  const [pairedDevices, setPairedDevices] = useState([]);
  const [escribirNombreManual, setEscribirNombreManual] = useState(false);

  const cargarDispositivosVinculados = () => {
    if (typeof window !== 'undefined' && window.AndroidPrinter && typeof window.AndroidPrinter.getPairedDevices === 'function') {
      try {
        const devicesJson = window.AndroidPrinter.getPairedDevices();
        const devices = JSON.parse(devicesJson || '[]');
        setPairedDevices(devices);
        return devices;
      } catch (err) {
        console.error('Error al obtener dispositivos vinculados Bluetooth:', err);
      }
    }
    return [];
  };

  useEffect(() => {
    cargarDispositivosVinculados();
  }, []);

  const mostrarMensajeExito = (msg) => {
    setExito(msg);
    setError('');
    setTimeout(() => setExito(''), 4000);
  };

  const mostrarMensajeError = (msg) => {
    setError(msg);
    setExito('');
    setTimeout(() => setError(''), 5000);
  };

  useEffect(() => {
    // Cargar configuraciones guardadas por usuario activo
    const lp = getLocalPrinter(userId);
    const isAppNative = typeof window !== 'undefined' && !!window.AndroidPrinter;
    if (isAppNative) {
      setSilentMode('silencioso', userId);
      setModoEmisionState('silencioso');
    } else {
      setModoEmisionState(getSilentModeType(userId));
    }
    setLocalPrinterState(lp);
    setFormatosPapelState(getPaperFormats(userId));
    setBtDeviceCache(getCachedBluetoothDevice());
    restoreBluetoothDevice(lp).then(dev => {
      if (dev) setBtDeviceCache(dev);
    });
  }, [userId, localPrinter]);

  // Vinculación y Activación 1-Clic de Impresora Bluetooth (permitirPicker = true)
  const handleVincularBluetooth = async () => {
    setVinculandoBt(true);
    setError('');
    try {
      const datosPrueba = {
        mesa: 'VINCULACION BT',
        piso: 1,
        mozo: userName,
        items: [{ cantidad: 1, nombre: 'ACTIVACION TICKETERA BLE' }],
        notaGeneral: 'Vinculación de Impresora Bluetooth (1-Clic)'
      };
      await imprimirViaBluetooth(datosPrueba, 'NUEVA', localPrinter, true);
      const dev = getCachedBluetoothDevice();
      setBtDeviceCache(dev);
      if (dev) {
        const updatedPrinter = {
          ...(localPrinter || {}),
          tipo_conexion: 'bluetooth',
          nombre: dev.name || localPrinter?.nombre || 'Ticketera Bluetooth'
        };
        setLocalPrinter(updatedPrinter, userId);
        setLocalPrinterState(updatedPrinter);
      }
      mostrarMensajeExito(`🟢 Impresora Bluetooth vinculada correctamente y activa en memoria viva: ${dev?.name || 'Desconocido'}`);
    } catch (err) {
      mostrarMensajeError(`Error al vincular impresora Bluetooth: ${err.message}`);
    } finally {
      setVinculandoBt(false);
    }
  };

  // Probar Conexión Red IP / Wi-Fi enviando un ticket de prueba TCP
  const handleProbarRedIP = async () => {
    setProbandoConexion(true);
    setError('');
    try {
      const ip = localPrinter?.ip || localPrinter?.direccion_ip || '192.168.1.200';
      const puerto = localPrinter?.puerto || 9100;
      const datosPrueba = {
        mesa: 'TEST RED IP',
        piso: 1,
        mozo: userName,
        ordenId: 'TCP-TEST',
        fecha: new Date().toLocaleDateString('es-PE'),
        hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        items: [
          { cantidad: 1, nombre: 'PRUEBA CONEXION IP TCP', categoria: 'RED IP', notas: `IP: ${ip}:${puerto}` }
        ],
        notaGeneral: `Ticket de Prueba de Conexión de Red IP / Wi-Fi (${ip}:${puerto})`
      };
      const res = await imprimirComandaCliente(datosPrueba, 'NUEVA');
      if (res && res.success !== false) {
        mostrarMensajeExito(`📡 Conexión a Red IP (${ip}:${puerto}) probada correctamente. Ticket enviado.`);
      } else {
        mostrarMensajeError(`Prueba de Red IP (${ip}:${puerto}): ${res?.message || 'No se recibió confirmación.'}`);
      }
    } catch (err) {
      mostrarMensajeError(`Error al probar conexión de Red IP: ${err.message}`);
    } finally {
      setProbandoConexion(false);
    }
  };

  // Imprimir Ticket de Prueba del Sistema (Kiosk / Background)
  const handleProbarSistema = async () => {
    setProbandoConexion(true);
    setError('');
    try {
      const datosPrueba = {
        mesa: 'TEST SISTEMA',
        piso: 1,
        mozo: userName,
        ordenId: 'SYS-TEST',
        fecha: new Date().toLocaleDateString('es-PE'),
        hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        items: [
          { cantidad: 1, nombre: 'PRUEBA IMPRESORA SISTEMA', categoria: 'SISTEMA', notas: 'Driver / Spooler OS' }
        ],
        notaGeneral: 'Ticket de Prueba de Impresora del Sistema'
      };
      const res = await imprimirComandaCliente(datosPrueba, 'NUEVA');
      if (res && res.success !== false) {
        mostrarMensajeExito('🖥️ Ticket de Prueba del Sistema enviado a la cola de impresión.');
      } else {
        mostrarMensajeError(`Prueba de Impresora del Sistema: ${res?.message || 'No se completó la impresión.'}`);
      }
    } catch (err) {
      mostrarMensajeError(`Error al imprimir ticket de prueba del sistema: ${err.message}`);
    } finally {
      setProbandoConexion(false);
    }
  };

  // Probar Conexión Bluetooth (Web o Nativo)
  const handleProbarBluetooth = async () => {
    setProbandoConexion(true);
    setError('');
    try {
      const nombreDisp = localPrinter?.nombre || 'Ticketera Bluetooth';
      const datosPrueba = {
        mesa: 'TEST BLUETOOTH',
        piso: 1,
        mozo: userName,
        ordenId: 'BT-TEST',
        fecha: new Date().toLocaleDateString('es-PE'),
        hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        items: [
          { cantidad: 1, nombre: 'PRUEBA CONEXION BLUETOOTH', categoria: 'BLUETOOTH', notas: `Dispositivo: ${nombreDisp}` }
        ],
        notaGeneral: `Ticket de Prueba de Conexión Bluetooth (${nombreDisp})`
      };
      const res = await imprimirComandaCliente(datosPrueba, 'NUEVA');
      if (res && res.success !== false) {
        mostrarMensajeExito(`🟢 Conexión a Bluetooth (${nombreDisp}) probada correctamente. Ticket enviado.`);
      } else {
        mostrarMensajeError(`Prueba de Bluetooth (${nombreDisp}): ${res?.message || 'No se recibió confirmación.'}`);
      }
    } catch (err) {
      mostrarMensajeError(`Error al probar conexión Bluetooth: ${err.message}`);
    } finally {
      setProbandoConexion(false);
    }
  };

  // Cambiar modo silencioso vs cuadro confirmación
  const handleToggleModoSilencioso = (nuevoModo) => {
    setSilentMode(nuevoModo, userId);
    setModoEmisionState(nuevoModo);
    if (nuevoModo === 'silencioso') {
      mostrarMensajeExito('🚀 Impresión Silenciosa Instantánea (Kiosk Mode) activada. Las comandas se emitirán en background sin cuadros de diálogo.');
    } else {
      mostrarMensajeExito('📄 Modo Cuadro de Confirmación activado. Se solicitará vista previa window.print() para inspección previa del usuario antes de enviar.');
    }
  };

  // Cambiar formato de papel de la ticketera local
  const handleCambiarFormatoArea = (ancho) => {
    setPaperFormat(null, null, ancho, userId);
    const updated = getPaperFormats(userId);
    setFormatosPapelState(updated);
    mostrarMensajeExito(`📏 Formato de papel actualizado a ${ancho}mm.`);
  };

  // Probar impresión en papel real con formato específico
  const handleProbarPapelReal = async (anchoPrueba, etiquetaArea = 'GENERAL') => {
    setProbandoFormato(anchoPrueba);
    const datosPrueba = {
      mesa: 'MESA 05',
      piso: pisoSeleccionado,
      mozo: 'Mozo Demo',
      ordenId: '1092',
      fecha: new Date().toLocaleDateString('es-PE'),
      hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
      anchoPapel: anchoPrueba,
      items: [
        { cantidad: 2, nombre: 'CEVICHE MIXTO', categoria: 'ENTRADAS', notas: 'Poco picante' },
        { cantidad: 1, nombre: 'LOMO SALTADO DE RES', categoria: 'PLATOS DE FONDO', notas: 'Termino medio' },
        { cantidad: 1, nombre: 'CHICHA MORADA 1L', categoria: 'BEBIDAS' }
      ],
      notaGeneral: `Prueba de Calibración Térmica - Formato ${anchoPrueba}mm (${etiquetaArea})`
    };

    try {
      const res = await imprimirComandaCliente(datosPrueba, 'NUEVA');
      if (res.success) {
        mostrarMensajeExito(`🖨️ Ticket de prueba de ${anchoPrueba}mm procesado correctamente (${res.method || 'éxito'}).`);
      } else {
        mostrarMensajeError(`Resultado de impresión: ${res.message || 'No se completó la impresión.'}`);
      }
    } catch (err) {
      mostrarMensajeError(`Error al ejecutar prueba de papel: ${err.message}`);
    } finally {
      setProbandoFormato(null);
    }
  };

  const areasDisponibles = [
    { id: 'todas', nombre: '🌐 Todas / General', icon: '🏪', desc: 'Configuración por defecto del piso' },
    { id: 'cocina', nombre: '🍳 Cocina', icon: '🍳', desc: 'Comandas de platos principales y entradas' },
    { id: 'barra', nombre: '🍸 Barra', icon: '🍸', desc: 'Tickets de bebidas, jugos y tragos' },
    { id: 'caja', nombre: '💵 Caja', icon: '💵', desc: 'Comprobantes y precuentas' }
  ];

  const pisosLista = [1, 2, 3, 4, 5];

  return (
    <div style={{
      padding: '1.75rem',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      color: '#0f172a'
    }}>

      {/* ENCABEZADO EJECUTIVO */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderRadius: 16,
        padding: '1.75rem 2rem',
        color: '#fff',
        marginBottom: '1.75rem',
        boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1.25rem'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={{ margin: 0, fontSize: '1.65rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#f8fafc' }}>
              Panel de Configuración de Impresión POS
            </h1>
          </div>
          <p style={{ margin: '0.5rem 0 0', color: '#94a3b8', fontSize: '0.95rem', maxWidth: '680px' }}>
            Gestión de formatos de papel térmico por Piso/Área, selector de emisión efectiva (Silenciosa Background vs Confirmación window.print) y guía de calibración multiplataforma.
          </p>
        </div>
      </div>

      <MsgError msg={error} />
      <MsgExito msg={exito} />


      {/* SECCIÓN 0: IMPRESORA ASIGNADA AL USUARIO EN SESIÓN */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        padding: '1.5rem 1.75rem',
        marginBottom: '1.75rem',
        boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
      }}>
        <div style={{ marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Impresora de {userName}
            </h2>
            <p style={{ margin: '0.3rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              Aislamiento de puerto, protocolo y ticketera por ID de Usuario ({userId || 'Sin ID'}).
            </p>
          </div>
          {localPrinter && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{
                background: localPrinter?.tipo_conexion === 'virtual' ? '#f3e8ff' : '#e0f2fe',
                color: localPrinter?.tipo_conexion === 'virtual' ? '#7e22ce' : '#0369a1',
                fontWeight: 800,
                fontSize: '0.78rem',
                padding: '0.3rem 0.75rem',
                borderRadius: 20
              }}>
                {localPrinter?.tipo_conexion === 'virtual' ? 'VIRTUAL KDS - SIN PAPEL' : `CONECTADO: ${(localPrinter.tipo_conexion || 'SISTEMA').toUpperCase()}`}
              </span>
              {(localPrinter?.tipo_conexion === 'ip' || localPrinter?.tipo_conexion === 'red') && (
                <span style={{
                  background: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  padding: '0.3rem 0.75rem',
                  borderRadius: 20
                }}>
                  {localPrinter?.ip || localPrinter?.direccion_ip || '192.168.1.200'}:{localPrinter?.puerto || 9100}
                </span>
              )}
            </div>
          )}
        </div>

        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>
                Tipo de Conexión
              </label>
              <select
                value={localPrinter?.tipo_conexion || 'virtual'}
                onChange={(e) => {
                  const updated = { ...(localPrinter || {}), tipo_conexion: e.target.value };
                  setLocalPrinter(updated, userId);
                  setLocalPrinterState(updated);
                  if (e.target.value === 'bluetooth') {
                    cargarDispositivosVinculados();
                  }
                  mostrarMensajeExito(`Tipo de conexión actualizado a ${e.target.value.toUpperCase()} para ${userName}`);
                }}
                style={{
                  width: '100%',
                  padding: '0.55rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#0f172a'
                }}
              >
                <option value="sistema">Impresora del Sistema (Kiosk / Background)</option>
                <option value="bluetooth">
                  {typeof window !== 'undefined' && window.AndroidPrinter 
                    ? 'Bluetooth Nativo (App Android)' 
                    : 'Web Bluetooth Directo (BLE)'}
                </option>
                <option value="ip">Impresora de Red IP Directa (Ethernet/Wi-Fi)</option>
                <option value="virtual">Modo Virtual (Sin Papel / Solo Comanda Digital Cocina/Barra)</option>
              </select>
            </div>

            {localPrinter?.tipo_conexion !== 'virtual' && (
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>
                  Nombre / Nombre de Dispositivo
                </label>
                {typeof window !== 'undefined' && window.AndroidPrinter && localPrinter?.tipo_conexion === 'bluetooth' ? (
                  escribirNombreManual ? (
                    <>
                      <input
                        type="text"
                        placeholder="Ej: Ticketera Personal Mozo"
                        value={localPrinter?.nombre || ''}
                        onChange={(e) => {
                          const updated = { ...(localPrinter || {}), nombre: e.target.value };
                          setLocalPrinter(updated, userId);
                          setLocalPrinterState(updated);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.55rem 0.75rem',
                          borderRadius: 8,
                          border: '1px solid #cbd5e1',
                          fontSize: '0.9rem',
                          color: '#0f172a'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setEscribirNombreManual(false);
                            cargarDispositivosVinculados();
                          }}
                          style={{
                            marginTop: '0.35rem',
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            fontWeight: 'bold',
                            fontSize: '0.78rem',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          Seleccionar de la lista
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <select
                        value={localPrinter?.nombre || ''}
                        onChange={(e) => {
                          const updated = { ...(localPrinter || {}), nombre: e.target.value };
                          setLocalPrinter(updated, userId);
                          setLocalPrinterState(updated);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.55rem 0.75rem',
                          borderRadius: 8,
                          border: '1px solid #cbd5e1',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#0f172a'
                        }}
                      >
                        <option value="">
                          {pairedDevices.length === 0 
                            ? '-- No se encontraron impresoras vinculadas --' 
                            : '-- Selecciona una Ticketera --'}
                        </option>
                        {pairedDevices.map((device, idx) => (
                          <option key={idx} value={device.name}>
                            {device.name} ({device.address})
                          </option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const devs = cargarDispositivosVinculados();
                            mostrarMensajeExito(`Lista actualizada: se encontraron ${devs.length} dispositivos.`);
                          }}
                          style={{
                            marginTop: '0.35rem',
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            fontWeight: 'bold',
                            fontSize: '0.78rem',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          Refrescar lista
                        </button>
                        <button
                          type="button"
                          onClick={() => setEscribirNombreManual(true)}
                          style={{
                            marginTop: '0.35rem',
                            background: 'none',
                            border: 'none',
                            color: '#475569',
                            fontWeight: 'bold',
                            fontSize: '0.78rem',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          Escribir manual
                        </button>
                      </div>
                    </>
                  )
                ) : (
                  <input
                    type="text"
                    placeholder="Ej: Ticketera Personal Mozo"
                    value={localPrinter?.nombre || ''}
                    onChange={(e) => {
                      const updated = { ...(localPrinter || {}), nombre: e.target.value };
                      setLocalPrinter(updated, userId);
                      setLocalPrinterState(updated);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.55rem 0.75rem',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      color: '#0f172a'
                    }}
                  />
                )}
              </div>
            )}

            {(localPrinter?.tipo_conexion === 'ip' || localPrinter?.tipo_conexion === 'red') && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>
                    Dirección IP
                  </label>
                  <input
                    type="text"
                    placeholder="192.168.1.200"
                    value={localPrinter?.ip || localPrinter?.direccion_ip || ''}
                    onChange={(e) => {
                      const updated = { ...(localPrinter || {}), ip: e.target.value, direccion_ip: e.target.value };
                      setLocalPrinter(updated, userId);
                      setLocalPrinterState(updated);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.55rem 0.75rem',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      color: '#0f172a'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>
                    Puerto IP
                  </label>
                  <input
                    type="number"
                    placeholder="9100"
                    value={localPrinter?.puerto || 9100}
                    onChange={(e) => {
                      const updated = { ...(localPrinter || {}), puerto: parseInt(e.target.value, 10) || 9100 };
                      setLocalPrinter(updated, userId);
                      setLocalPrinterState(updated);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.55rem 0.75rem',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      color: '#0f172a'
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            {localPrinter && (
              <button
                onClick={() => {
                  clearLocalPrinter(userId);
                  clearSavedBluetoothDeviceName();
                  setCachedBluetoothDevice(null);
                  setBtDeviceCache(null);
                  setLocalPrinterState(null);
                  mostrarMensajeExito(`Impresora de ${userName} desvinculada.`);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 8,
                  border: '1px solid #f43f5e',
                  background: '#fff1f2',
                  color: '#e11d48',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Restablecer Impresora de {userName}
              </button>
            )}
          </div>

          {/* Indicador de estado y Botón de Acción Dinámica (Bluetooth, Red IP, Sistema) */}
          {((localPrinter?.tipo_conexion || 'sistema') === 'bluetooth') && (
            <div style={{
              marginTop: '1.25rem',
              padding: '1.1rem',
              borderRadius: 12,
              background: (typeof window !== 'undefined' && window.AndroidPrinter)
                ? (localPrinter?.nombre ? '#f0fdf4' : '#f8fafc')
                : (btDeviceCache ? '#f0fdf4' : (getSavedBluetoothDeviceName() ? '#fffbeb' : '#f8fafc')),
              border: (typeof window !== 'undefined' && window.AndroidPrinter)
                ? (localPrinter?.nombre ? '1.5px solid #86efac' : '1px dashed #cbd5e1')
                : (btDeviceCache ? '1.5px solid #86efac' : (getSavedBluetoothDeviceName() ? '1.5px solid #fcd34d' : '1px dashed #cbd5e1'))
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.85rem' }}>
                <div style={{
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  color: (typeof window !== 'undefined' && window.AndroidPrinter)
                    ? (localPrinter?.nombre ? '#166534' : '#475569')
                    : (btDeviceCache ? '#166534' : (getSavedBluetoothDeviceName() ? '#92400e' : '#475569')),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {typeof window !== 'undefined' && window.AndroidPrinter ? (
                    localPrinter?.nombre ? (
                      <span>BLUETOOTH NATIVO ACTIVO: <strong>{localPrinter.nombre}</strong></span>
                    ) : (
                      <span>Ninguna Impresora Bluetooth seleccionada de la lista superior</span>
                    )
                  ) : btDeviceCache ? (
                    <span>BLUETOOTH ACTIVO (RESTAURADO AUTOMÁTICAMENTE): <strong>{btDeviceCache.name || 'Desconocido'}</strong></span>
                  ) : getSavedBluetoothDeviceName() ? (
                    <span>IMPRESORA CONFIGURADA: <strong>{getSavedBluetoothDeviceName()}</strong> &mdash; <em style={{ fontWeight: 400, fontSize: '0.85rem' }}>Imprime para reconectar automáticamente</em></span>
                  ) : (
                    <span>Ninguna Impresora Bluetooth vinculada en la memoria viva de esta sesión</span>
                  )}
                </div>
                {((typeof window === 'undefined' || !window.AndroidPrinter) && btDeviceCache) && (
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.75rem', fontWeight: 800, padding: '0.2rem 0.6rem', borderRadius: 12 }}>
                    SINGLETON CACHE OK
                  </span>
                )}
                {((typeof window === 'undefined' || !window.AndroidPrinter) && !btDeviceCache && getSavedBluetoothDeviceName()) && (
                  <span style={{ background: '#fef9c3', color: '#854d0e', fontSize: '0.75rem', fontWeight: 800, padding: '0.2rem 0.6rem', borderRadius: 12 }}>
                    GUARDADO EN LOCAL
                  </span>
                )}
                {(typeof window !== 'undefined' && window.AndroidPrinter && localPrinter?.nombre) && (
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.75rem', fontWeight: 800, padding: '0.2rem 0.6rem', borderRadius: 12 }}>
                    CONECTADO NATIVO
                  </span>
                )}
              </div>

              {typeof window !== 'undefined' && window.AndroidPrinter ? (
                <div style={{
                  padding: '0.75rem 1rem',
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.88rem',
                  color: '#475569',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>La ticketera Bluetooth se administra en los Ajustes de Android. Vincula tu impresora en tu tablet y elígela en la lista desplegable de arriba.</span>
                </div>
              ) : (
                <button
                  onClick={handleVincularBluetooth}
                  disabled={vinculandoBt}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1.25rem',
                    borderRadius: 10,
                    border: 'none',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    cursor: vinculandoBt ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.6rem',
                    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {vinculandoBt ? 'Vinculando Impresora Bluetooth...' : 'Vincular / Activar Impresora Bluetooth (1-Clic)'}
                </button>
              )}

              <button
                onClick={handleProbarBluetooth}
                disabled={probandoConexion}
                style={{
                  width: '100%',
                  marginTop: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  cursor: probandoConexion ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.6rem',
                  boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                {probandoConexion ? 'Enviando Ticket de Prueba...' : 'Probar Conexión Bluetooth (1-Clic)'}
              </button>
            </div>
          )}

          {(localPrinter?.tipo_conexion === 'ip' || localPrinter?.tipo_conexion === 'red') && (() => {
            const isAppNative = typeof window !== 'undefined' && !!window.AndroidPrinter;
            return (
              <div style={{
                marginTop: '1.25rem',
                padding: '1.1rem',
                borderRadius: 12,
                background: isAppNative ? '#f0f9ff' : '#fff5f5',
                border: isAppNative ? '1.5px solid #7dd3fc' : '1.5px solid #fca5a5'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.85rem' }}>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem', color: isAppNative ? '#0369a1' : '#9f1239', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>CONFIGURACIÓN RED IP / WI-FI DIRECTA</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ background: isAppNative ? '#e0f2fe' : '#ffe4e6', color: isAppNative ? '#0284c7' : '#be123c', fontSize: '0.78rem', fontWeight: 800, padding: '0.2rem 0.65rem', borderRadius: 12 }}>
                      IP: {localPrinter?.ip || localPrinter?.direccion_ip || '192.168.1.200'}
                    </span>
                    <span style={{ background: isAppNative ? '#e0f2fe' : '#ffe4e6', color: isAppNative ? '#0284c7' : '#be123c', fontSize: '0.78rem', fontWeight: 800, padding: '0.2rem 0.65rem', borderRadius: 12 }}>
                      PUERTO: {localPrinter?.puerto || 9100}
                    </span>
                    <span style={{ 
                      background: isAppNative ? '#dcfce7' : '#fecdd3', 
                      color: isAppNative ? '#15803d' : '#9f1239', 
                      fontSize: '0.78rem', 
                      fontWeight: 800, 
                      padding: '0.2rem 0.65rem', 
                      borderRadius: 12 
                    }}>
                      {isAppNative ? 'RAW TCP OK' : 'BLOQUEADO EN WEB'}
                    </span>
                  </div>
                </div>

                {!isAppNative && (
                  <div style={{
                    padding: '0.85rem 1rem',
                    borderRadius: 10,
                    background: '#fff1f2',
                    border: '1px solid #fecdd3',
                    fontSize: '0.88rem',
                    color: '#9f1239',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    lineHeight: '1.4'
                  }}>
                    <span>
                      <strong>Modo Bloqueado:</strong> La impresión por Red IP Directa no está soportada en navegadores web debido a restricciones de seguridad de Google Chrome (Mixed Content y Private Network Access). Por favor, <strong>instala y utiliza el aplicativo APK nativo</strong> en tu tablet o celular para usar este modo.
                    </span>
                  </div>
                )}

                <button
                  onClick={isAppNative ? handleProbarRedIP : undefined}
                  disabled={probandoConexion || !isAppNative}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1.25rem',
                    borderRadius: 10,
                    border: 'none',
                    background: isAppNative ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#cbd5e1',
                    color: isAppNative ? '#fff' : '#94a3b8',
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    cursor: !isAppNative ? 'not-allowed' : (probandoConexion ? 'wait' : 'pointer'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.6rem',
                    boxShadow: isAppNative ? '0 4px 14px rgba(2,132,199,0.3)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {probandoConexion ? 'Enviando Ticket TCP de Prueba...' : !isAppNative ? 'Modo Red IP deshabilitado en navegador' : 'Probar Conexión de Red IP / Wi-Fi (1-Clic)'}
                </button>
              </div>
            );
          })()}

          {(localPrinter?.tipo_conexion === 'sistema' || !localPrinter?.tipo_conexion) && (
            <div style={{
              marginTop: '1.25rem',
              padding: '1.1rem',
              borderRadius: 12,
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.85rem' }}>
                <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>IMPRESORA PREDETERMINADA DEL SISTEMA (KIOSK / OS SPOOL)</span>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <span style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.78rem', fontWeight: 800, padding: '0.2rem 0.65rem', borderRadius: 12 }}>
                    DRIVER LOCAL
                  </span>
                  <span style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.78rem', fontWeight: 800, padding: '0.2rem 0.65rem', borderRadius: 12 }}>
                    MODO SILENCIOSO
                  </span>
                </div>
              </div>

              <button
                onClick={handleProbarSistema}
                disabled={probandoConexion}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.25rem',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  cursor: probandoConexion ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.6rem',
                  boxShadow: '0 4px 14px rgba(71,85,105,0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                {probandoConexion ? 'Imprimiendo Ticket de Prueba del Sistema...' : 'Imprimir Ticket de Prueba del Sistema (1-Clic)'}
              </button>
            </div>
          )}

          {(!localPrinter || localPrinter?.tipo_conexion === 'virtual') && (
            <div style={{
              marginTop: '1.25rem',
              padding: '1.1rem',
              borderRadius: 12,
              background: '#f3e8ff',
              border: '1.5px solid #c084fc'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.85rem' }}>
                <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#6b21a8', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>MODO VIRTUAL KDS ACTIVO (SIN PAPEL FÍSICO)</span>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <span style={{ background: '#e9d5ff', color: '#7e22ce', fontSize: '0.78rem', fontWeight: 800, padding: '0.2rem 0.65rem', borderRadius: 12 }}>
                    KDS DIGITAL
                  </span>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.78rem', fontWeight: 800, padding: '0.2rem 0.65rem', borderRadius: 12 }}>
                    AHORRO PAPEL 100%
                  </span>
                </div>
              </div>

              <button
                disabled
                style={{
                  width: '100%',
                  padding: '0.75rem 1.25rem',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  cursor: 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.6rem',
                  boxShadow: '0 4px 14px rgba(109,40,217,0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                Modo Virtual Activo (Comandas 100% Digitales en Pantalla)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SECCIÓN 1: SELECTOR DE MODO DE IMPRESIÓN (SILENCIOSA VS CONFIRMACIÓN) */}
      {!(typeof window !== 'undefined' && !!window.AndroidPrinter) && (
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
          padding: '1.5rem 1.75rem',
          marginBottom: '1.75rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
        }}>
          <div style={{ marginBottom: '1.25rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Modo de Emisión de Comandas
            </h2>
            <p style={{ margin: '0.3rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              Elija el comportamiento del motor al enviar comandas desde la pantalla de pedidos o mozos.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
            {/* Card Modo Silencioso */}
            <div
              onClick={() => handleToggleModoSilencioso('silencioso')}
              style={{
                border: modoEmision === 'silencioso' ? '2.5px solid #10b981' : '1.5px solid #cbd5e1',
                background: modoEmision === 'silencioso' ? '#f0fdf4' : '#fff',
                borderRadius: 14,
                padding: '1.25rem 1.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
            >
              {modoEmision === 'silencioso' && (
                <span style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#10b981',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  padding: '0.2rem 0.6rem',
                  borderRadius: 12
                }}>
                  ACTIVO
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: modoEmision === 'silencioso' ? '#065f46' : '#1e293b' }}>
                  Impresión Silenciosa Instantánea
                </h3>
              </div>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.88rem', lineHeight: '1.5' }}>
                <strong>Kiosk Mode / Background:</strong> Envío directo a la impresora térmica (vía Web Bluetooth, Red IP o iframe oculto) sin mostrar cuadros de diálogo. Ideal para máxima velocidad en horas punta.
              </p>
            </div>

            {/* Card Modo Confirmación */}
            <div
              onClick={() => handleToggleModoSilencioso('confirmacion')}
              style={{
                border: modoEmision === 'confirmacion' ? '2.5px solid #2563eb' : '1.5px solid #cbd5e1',
                background: modoEmision === 'confirmacion' ? '#eff6ff' : '#fff',
                borderRadius: 14,
                padding: '1.25rem 1.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
            >
              {modoEmision === 'confirmacion' && (
                <span style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#2563eb',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  padding: '0.2rem 0.6rem',
                  borderRadius: 12
                }}>
                  ACTIVO
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: modoEmision === 'confirmacion' ? '#1e40af' : '#1e293b' }}>
                  Cuadro de Confirmación
                </h3>
              </div>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.88rem', lineHeight: '1.5' }}>
                <strong>Previsualización window.print():</strong> Invoca la ventana de previsualización antes de emitir la orden para permitir inspección previa del usuario.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN 2: FORMATO DE PAPEL DE LA TICKETERA LOCAL */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        padding: '1.5rem 1.75rem',
        marginBottom: '1.75rem',
        boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Formato de Papel de la Ticketera Local
            </h2>
            <p style={{ margin: '0.3rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              Defina el ancho térmico único (80mm Estándar o 58mm Compacto) para todas las impresiones emitidas desde este equipo.
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <span style={{ 
              background: formatosPapel === 80 ? '#e0e7ff' : '#fef3c7', 
              color: formatosPapel === 80 ? '#3730a3' : '#92400e', 
              fontSize: '0.78rem', 
              fontWeight: 800, 
              padding: '0.2rem 0.65rem', 
              borderRadius: 12 
            }}>
              FORMATO ACTIVO: {formatosPapel} mm
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '1.25rem' }}>
          {/* Card Config Formato */}
          <div style={{
            background: '#f8fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: 14,
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '1rem'
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a', marginBottom: '0.25rem' }}>
                Selecciona el ancho térmico
              </div>
              <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Esto calibrará el diseño de los comprobantes, comandas y cortes de caja para que encajen en tu ticketera.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', background: '#e2e8f0', padding: '0.25rem', borderRadius: 9 }}>
              <button
                onClick={() => handleCambiarFormatoArea(80)}
                style={{
                  flex: 1,
                  padding: '0.55rem 0',
                  borderRadius: 7,
                  border: 'none',
                  background: formatosPapel === 80 ? '#fff' : 'transparent',
                  color: formatosPapel === 80 ? '#0f172a' : '#64748b',
                  fontWeight: formatosPapel === 80 ? 800 : 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  boxShadow: formatosPapel === 80 ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                80 mm (Ancho Estándar)
              </button>
              <button
                onClick={() => handleCambiarFormatoArea(58)}
                style={{
                  flex: 1,
                  padding: '0.55rem 0',
                  borderRadius: 7,
                  border: 'none',
                  background: formatosPapel === 58 ? '#fff' : 'transparent',
                  color: formatosPapel === 58 ? '#0f172a' : '#64748b',
                  fontWeight: formatosPapel === 58 ? 800 : 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  boxShadow: formatosPapel === 58 ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                58 mm (Estrecho Compacto)
              </button>
            </div>
          </div>

          {/* Card Prueba Rápida */}
          <div style={{
            background: formatosPapel === 80 ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            border: formatosPapel === 80 ? '1px solid #cbd5e1' : '1px solid #fed7aa',
            borderRadius: 14,
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '1rem'
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: formatosPapel === 80 ? '#0f172a' : '#9a3412', marginBottom: '0.25rem' }}>
                Imprimir Ticket de Prueba
              </div>
              <div style={{ fontSize: '0.82rem', color: formatosPapel === 80 ? '#64748b' : '#c2410c' }}>
                Envía una comanda ficticia de demostración para verificar que el texto no se recorte y los márgenes sean correctos.
              </div>
            </div>

            <button
              onClick={() => handleProbarPapelReal(formatosPapel, 'TEST LOCAL')}
              disabled={probandoFormato === formatosPapel}
              style={{
                width: '100%',
                padding: '0.65rem 1.15rem',
                borderRadius: 9,
                border: 'none',
                background: formatosPapel === 80 ? '#0f172a' : '#ea580c',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: probandoFormato ? 'wait' : 'pointer',
                boxShadow: formatosPapel === 80 ? '0 4px 12px rgba(15,23,42,0.15)' : '0 4px 12px rgba(234,88,12,0.2)',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {probandoFormato ? 'Imprimiendo...' : `Probar Ticket de ${formatosPapel}mm (1-Clic)`}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ConfiguracionComponent;
